import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { clearSecureSession, rootApi } from './RootScurityClient';

const AUTH_KEYS = [
  'user', 'user_role', 'auth_timestamp', 'current_shop', 'session_id',
  'is_super_admin', 'user_subscription',
];

const TERMINAL_SESSION_CODES = new Set([
  'SESSION_REPLACED', 'SESSION_REVOKED', 'SESSION_EXPIRED',
  'AUTH_REQUIRED', 'IP_BLOCKED', 'IP_NOT_APPROVED', 'USER_INACTIVE',
  'APPLICATION_ACCESS_REVOKED',
]);

const normalizeRole = (role) => {
  const value = String(role || '').trim().toLowerCase();
  return value === 'superadmin' ? 'super_admin' : value;
};

const homeForRole = (role) => (
  normalizeRole(role) === 'super_admin'
    ? '/DashboardSuperAdmin'
    : '/Dashboard-Business-Manager'
);

function clearAuthentication() {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  clearSecureSession(true);
}

export default function PrivateRoute({ children, requiredRole, allowedRoles }) {
  const { user, loading, setUser } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('');
  const [attempt, setAttempt] = useState(0);

  const userId = Number(user?.id || 0);
  const userRole = normalizeRole(user?.role);
  const applicationId = Number(user?.application_id || 0);
  const requiredRoleKey = normalizeRole(requiredRole);
  const allowedRoleKey = useMemo(
    () => (Array.isArray(allowedRoles) ? allowedRoles.map(normalizeRole).sort().join('|') : ''),
    [allowedRoles]
  );

  useEffect(() => {
    let alive = true;

    const update = (nextStatus, nextMessage = '') => {
      if (!alive) return;
      setMessage(nextMessage);
      setStatus(nextStatus);
    };

    const endAuthentication = (reason) => {
      console.warn(reason);
      clearAuthentication();
      update('unauthenticated', reason);
    };

    const validateAccess = async () => {
      if (loading) return update('checking');
      if (!userId || !userRole) return endAuthentication('Authentication required');

      // The bearer session is authoritative. Repair stale local role metadata
      // instead of logging out a valid user because two localStorage keys differ.
      if (normalizeRole(localStorage.getItem('user_role')) !== userRole) {
        localStorage.setItem('user_role', userRole);
      }

      const allowed = allowedRoleKey ? allowedRoleKey.split('|') : [];
      if (requiredRoleKey && userRole !== requiredRoleKey) {
        return update('forbidden', 'Your account does not have permission for this page.');
      }
      if (allowed.length && !allowed.includes(userRole)) {
        return update('forbidden', 'Your account does not have permission for this page.');
      }

      update('checking');
      try {
        const result = await rootApi('root_session_api.php?action=heartbeat');
        if (!alive) return;

        if (Number(result.session?.user_id) !== userId) {
          return endAuthentication('Session user does not match the logged-in user.');
        }

        const resolvedApplicationId = Number(
          result.session?.application_id
          || applicationId
          || user?.subscription?.summary?.subscriptions?.active?.[0]?.application_id
          || user?.subscription?.summary?.subscriptions?.grace_period?.[0]?.application_id
          || user?.subscription?.subscriptions?.active?.[0]?.application_id
          || user?.subscription?.subscriptions?.grace_period?.[0]?.application_id
          || 0
        ) || null;

        if (!applicationId && resolvedApplicationId) {
          setUser({ ...user, application_id: resolvedApplicationId });
        }
        localStorage.setItem('auth_timestamp', Date.now().toString());
        update('allowed');
      } catch (error) {
        const code = error?.data?.code;
        const errorMessage = error?.data?.message || error?.data?.error || error?.message
          || 'Could not verify the server session.';

        // Only an authoritative authentication response may erase the login.
        // Network failures, server errors and ordinary 403 permissions do not.
        if (error?.status === 401 || TERMINAL_SESSION_CODES.has(code)) {
          return endAuthentication(errorMessage);
        }
        console.error('Session verification temporarily failed:', error);
        update('unavailable', errorMessage);
      }
    };

    validateAccess();
    return () => { alive = false; };
  }, [
    allowedRoleKey, applicationId, attempt, loading, requiredRoleKey,
    setUser, user, userId, userRole,
  ]);

  // Keep the saved workspace mounted while the heartbeat verifies it in the
  // background. The authoritative result below still handles invalid sessions.
  if (loading || status === 'checking') return children;

  if (status === 'unauthenticated') {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (status === 'forbidden') {
    const home = homeForRole(userRole);
    if (location.pathname !== home) return <Navigate to={home} replace />;
    return <div className="route-access-error"><h2>Access denied</h2><p>{message}</p></div>;
  }

  if (status === 'unavailable') {
    return (
      <div className="route-access-error" role="alert">
        <h2>Connection problem</h2>
        <p>{message}</p>
        <button type="button" onClick={() => setAttempt((value) => value + 1)}>Retry</button>
      </div>
    );
  }

  return children;
}
